import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Divider, useBreakpointValue } from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import { DATA_TYPES, buildSeries } from "./feeChartConfig";
import { FeeHeader, FeeSelectors, ChartContainer, YearTotalLabel, FeeYearNavigation, MixedTotalNote } from "./feeDiagramComponents";
import { useAdminPalette } from "./adminPalette";

/**
 * Returns the localized label for the selected data type.
 * @param {string} selectedType - Current data type selection
 * @returns {string} Localized type label
 */
function getTypeLabel(selectedType) {
    const labels = { [DATA_TYPES.RESERVATIONS]: "Réservations", [DATA_TYPES.CONTACTS]: "Prises de contact", [DATA_TYPES.TOTAL]: "Total" };
    return labels[selectedType] || "";
}

/**
 * Computes the year total for the selected currency and data type.
 * @param {Object|null} incomeData - Income data from the API
 * @param {string} selectedCurrency - Selected currency code
 * @param {string} selectedType - Selected data type
 * @returns {number} Year total amount
 */
function computeYearTotal(incomeData, selectedCurrency, selectedType) {
    const typeKey = selectedType === DATA_TYPES.TOTAL ? 'total' : selectedType;
    return incomeData?.totals?.[selectedCurrency]?.[typeKey] || 0;
}

/**
 * Dashboard component displaying fee income diagrams with currency and type selectors.
 * Shows monthly revenue data with year navigation.
 */
function FeeDiagrams() {
    const [loading, setLoading] = useState(true);
    const [incomeData, setIncomeData] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState("");
    const [selectedType, setSelectedType] = useState(DATA_TYPES.TOTAL);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    const isMobile = useBreakpointValue({ base: true, sm: false });
    const palette = useAdminPalette();

    /**
     * Fetches income data for a given year from the API.
     * @param {number} [year] - Year to fetch, defaults to currentYear
     */
    const checkIncomeByYear = useCallback(async (year) => {
        setLoading(true);
        try {
            const response = await axios.get(`/admin/reservation/income_of_year/${year || currentYear}`);
            setIncomeData(response.data);
            if (response.data.currencies?.length > 0 && !selectedCurrency) {
                setSelectedCurrency(response.data.currencies[0]);
            }
        } catch (error) {
            console.error("Failed to fetch income stats:", error);
        } finally {
            setLoading(false);
        }
    }, [currentYear, selectedCurrency]);

    /**
     * Navigates to adjacent year and fetches data.
     * @param {number} nbr - Year offset (+1 or -1)
     */
    const changeYear = useCallback(async (nbr) => {
        const newYear = currentYear + nbr;
        setCurrentYear(newYear);
        await checkIncomeByYear(newYear);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [currentYear]);

    useEffect(() => {
        checkIncomeByYear();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    const yearTotal = computeYearTotal(incomeData, selectedCurrency, selectedType);
    const series = buildSeries(incomeData, selectedCurrency, selectedType);

    return (
        <Card display="flex" flexDirection="column" h="100%" p="0" overflow="hidden">
            <Box padding={isMobile ? "16px" : "20px"} display="flex" flexDirection="column" flexGrow={1}>
                <FeeHeader isMobile={isMobile} currentYear={currentYear} loading={loading} onRefresh={() => checkIncomeByYear()} />
                <Divider borderColor={palette.border} mb={isMobile ? 3 : 4} />
                {incomeData?.currencies?.length > 0 && (
                    <FeeSelectors
                        selectedType={selectedType} onTypeChange={(v) => v && setSelectedType(v)}
                        selectedCurrency={selectedCurrency} onCurrencyChange={(e) => setSelectedCurrency(e.target.value)}
                        currencies={incomeData.currencies} isMobile={isMobile}
                    />
                )}
                <ChartContainer loading={loading} series={series} isMobile={isMobile} />
                {selectedType === DATA_TYPES.TOTAL && <MixedTotalNote isMobile={isMobile} />}
                <Divider borderColor={palette.border} mb={isMobile ? 3 : 4} />
                <YearTotalLabel typeLabel={getTypeLabel(selectedType)} year={currentYear} total={yearTotal} currency={selectedCurrency} isMobile={isMobile} />
                <FeeYearNavigation loading={loading} currentYear={currentYear} onChangeYear={changeYear} onRefresh={() => checkIncomeByYear()} isMobile={isMobile} />
            </Box>
        </Card>
    );
}

export default FeeDiagrams;
